/**
 * Gas Station Service
 * Hybrid approach: Curated database for brands, OpenStreetMap for "all"
 */

import { CuratedStationsService } from './CuratedStationsService.js';
import { w, currentMarket } from './market.js';

// Traveller votes needed before a station counts as RV-friendly.
const RV_CONFIRMATIONS = 3;

// Fuel markers on the map for one route (50 crowded a 545-mile route).
const MAX_STATION_MARKERS = 25;

// Australian fuel chains for the fuel filter (AU site / Australian accounts).
// Matched on the OpenStreetMap name and brand/operator, since many stations
// carry the brand only in their name. Caltex sites are being rebranded Ampol.
export const AU_FUEL_CHAINS = {
    ampol: /\b(ampol|caltex)\b/,
    bp: /\bbp\b/,
    shell: /\b(shell|coles express|reddy express)\b/,
    '7eleven': /\b7[\s-]?eleven\b/,
    united: /\bunited( petroleum)?\b/,
    liberty: /\bliberty\b/,
    mobil: /\bmobil\b/,
    metro: /\bmetro (petroleum|fuel)\b/,
    otr: /\b(otr|on the run)\b/,
    puma: /\bpuma\b/
};

export function matchesAuChain(station, chainKeys) {
    const text = `${station.name || ''} ${station.brand || ''}`.toLowerCase();
    return chainKeys.some((k) => AU_FUEL_CHAINS[k]?.test(text));
}

export class GasStationService {
    constructor() {
        // Curated database for brand searches (free, accurate)
        this.curatedService = new CuratedStationsService();
        
        // Use backend API for gas station searches (Google Places via Lambda)
        this.useBackend = import.meta.env.VITE_USE_BACKEND === 'true';
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
        
        // Overpass API for fallback only
        this.overpassUrls = [
            'https://overpass-api.de/api/interpreter',
            'https://overpass.kumi.systems/api/interpreter',
            'https://overpass.openstreetmap.ru/api/interpreter'
        ];
        this.currentUrlIndex = 0;
        
        // Cache for station data
        this.stationCache = new Map();
        
        console.log('🎯 Gas Station Service initialized');
        console.log('Using backend API:', this.useBackend);
        
        // Log stats when loaded
        this.curatedService.getStats().then(stats => {
            console.log('📊 Curated database stats:', stats);
        });
    }

    /**
     * Attach traveller pull-through votes and decide RV-friendliness from them.
     * A station counts once RV_CONFIRMATIONS travellers say yes and they outnumber
     * the no answers. On the AU site only votes count: hgv tags there mark truck
     * stops, whose high-flow pumps often don't fit a ute or 4WD.
     */
    async applyTravelerRatings(stations) {
        const ids = stations.map(s => s.verifyId).filter(Boolean);
        const summaries = {};
        try {
            for (let i = 0; i < ids.length; i += 200) {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);
                const res = await fetch(`${this.backendUrl}/api/fuel-stops/summary`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids: ids.slice(i, i + 200) }),
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
                if (res.ok) Object.assign(summaries, (await res.json()).summaries || {});
            }
        } catch (err) {
            console.warn('Traveller ratings unavailable:', err.message);
        }

        for (const s of stations) {
            const r = summaries[s.verifyId] || { yes: 0, no: 0, votes: 0 };
            const confirmed = r.yes >= RV_CONFIRMATIONS && r.yes > r.no;
            const tagged = s.taggedRvFriendly ?? s.source === 'curated';
            s.ratings = r;
            s.travelerConfirmed = confirmed;
            s.isRvFriendly = currentMarket() === 'AU' ? confirmed : (tagged || confirmed);
        }
        return stations;
    }

    get overpassUrl() {
        return this.overpassUrls[this.currentUrlIndex];
    }

    /**
     * Find gas stations along a route
     * Strategy: Use curated DB for brand searches, OSM for "all" and "rv-friendly"
     */
    async findGasStationsAlongRoute(coordinates, options = {}) {
        const {
            rvFriendly = false,
            brand = null,
            auChains = null, // Australian chain keys (AU_FUEL_CHAINS); filtered before the 50-station cap
            maxDistance = 0.5,
            sampleInterval = 20
        } = options;

        // Use curated database ONLY for brand-specific searches
        if (brand) {
            console.log('📚 Using curated database for brand search:', brand);
            const curated = await this.curatedService.findStationsAlongRoute(coordinates, {
                brand,
                maxDistance: 5.0 // Larger radius for brand searches
            });
            return this.applyTravelerRatings(curated);
        }

        // Use OpenStreetMap for "all" and "rv-friendly" (nationwide coverage)
        console.log('🗺️ Using OpenStreetMap for nationwide coverage');
        return this.findStationsWithOSM(coordinates, rvFriendly, maxDistance, auChains);
    }

    /**
     * Find stations using OpenStreetMap (for "all" and "rv-friendly")
     */
    /**
     * Fuel stations along the route from our own database, in Overpass's
     * response shape ({ elements }). Returns null on any failure so the
     * caller falls back to Overpass.
     */
    async fetchZephyrStations(coordinates) {
        try {
            const step = Math.max(1, Math.ceil(coordinates.length / 2000));
            const points = coordinates
                .filter((_, i) => i % step === 0 || i === coordinates.length - 1)
                .map(c => [c.lng ?? c[1], c.lat ?? c[0]]);
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            const res = await fetch(`${this.backendUrl}/api/fuel-stations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ points }),
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (!res.ok) return null;
            const data = await res.json();
            return Array.isArray(data.elements) ? data : null;
        } catch (e) {
            console.warn('Zephyr fuel stations unavailable, using Overpass:', e.message);
            return null;
        }
    }

    async findStationsWithOSM(coordinates, rvFriendly, maxDistance, auChains = null) {
        try {
            console.log('🔍 Searching OpenStreetMap');
            
            // Create bounding box around route
            const bbox = this.createBoundingBox(coordinates, maxDistance);
            const cacheKey = `${bbox.south},${bbox.west},${bbox.north},${bbox.east}`;
            
            // Check cache first
            let allStations;
            if (this.stationCache.has(cacheKey)) {
                console.log('✅ Using cached OSM data');
                allStations = this.stationCache.get(cacheKey);
            } else {
                // Our own station database first (built from OpenStreetMap
                // extracts, lambda/fuel-stations); the public Overpass servers
                // only if it fails -- they are often at capacity.
                let data = await this.fetchZephyrStations(coordinates);
                if (!data) {
                    const query = this.buildSimpleQuery(bbox);

                    console.log('📡 Fetching from Overpass API...');

                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 8000); // Reduced to 8 seconds

                    const response = await fetch(this.overpassUrl, {
                        method: 'POST',
                        body: query,
                        signal: controller.signal,
                        headers: { 'Content-Type': 'text/plain' }
                    });

                    clearTimeout(timeoutId);

                    if (!response.ok) {
                        throw new Error(`Overpass API error: ${response.status}`);
                    }

                    data = await response.json();
                }
                console.log('📊 Received', data.elements?.length || 0, 'stations');
                
                // Process all stations
                allStations = this.processStations(data.elements, coordinates, maxDistance);
                
                // Cache for 5 minutes
                this.stationCache.set(cacheKey, allStations);
                setTimeout(() => this.stationCache.delete(cacheKey), 5 * 60 * 1000);
            }
            
            // At most MAX_STATION_MARKERS, spread over the whole route. Taking the first
            // 50 along the route put them all in the starting city (Denver to
            // Salt Lake City: all 50 within the first 0.3 miles).
            let shown;
            if (auChains?.length) {
                shown = this.spreadAlongRoute(allStations.filter(s => matchesAuChain(s, auChains)), MAX_STATION_MARKERS);
            } else if (rvFriendly) {
                shown = this.spreadAlongRoute(allStations.filter(s => s.isRvFriendly), MAX_STATION_MARKERS);
                console.log(`🚛 Filtered to ${shown.length} RV-friendly stations`);
            } else {
                shown = this.spreadAlongRoute(allStations, MAX_STATION_MARKERS);
            }
            await this.applyTravelerRatings(shown);
            return shown;
            
        } catch (error) {
            if (error.name === 'AbortError') {
                console.error('⚠️ Request timed out after 8 seconds');
                // Try next Overpass server
                this.currentUrlIndex = (this.currentUrlIndex + 1) % this.overpassUrls.length;
                console.log(`🔄 Switching to backup server: ${this.overpassUrl}`);
            } else {
                console.error('❌ Error:', error);
            }
            // Return empty array instead of retrying to avoid long waits
            return [];
        }
    }

    /**
     * Build simple query - just get all stations (fastest)
     */
    buildSimpleQuery(bbox) {
        const { south, north, west, east } = bbox;
        const bboxStr = `${south},${west},${north},${east}`;
        
        return `[out:json][timeout:10];
node["amenity"="fuel"](${bboxStr});
out body;`;
    }
    /**
     * Create bounding box around route
     */
    createBoundingBox(coordinates, bufferMiles) {
        const bufferDegrees = bufferMiles / 69; // Rough conversion: 1 degree ≈ 69 miles
        
        let minLat = Infinity, maxLat = -Infinity;
        let minLng = Infinity, maxLng = -Infinity;

        coordinates.forEach(coord => {
            minLat = Math.min(minLat, coord.lat);
            maxLat = Math.max(maxLat, coord.lat);
            minLng = Math.min(minLng, coord.lng);
            maxLng = Math.max(maxLng, coord.lng);
        });

        return {
            south: minLat - bufferDegrees,
            north: maxLat + bufferDegrees,
            west: minLng - bufferDegrees,
            east: maxLng + bufferDegrees
        };
    }

    /**
     * Process station data from Overpass API
     */
    processStations(elements, routeCoordinates, maxDistance) {
        if (!elements || elements.length === 0) {
            console.log('No elements returned from Overpass API');
            return [];
        }
        
        const stations = [];
        const maxDistanceMiles = maxDistance;

        elements.forEach(element => {
            // Pinned stations (nodes), and traced ones (ways) that our station
            // database returns at their centre. Anything without a position
            // (e.g. a way straight from Overpass) is skipped.
            if (element.type !== 'node' && element.type !== 'way') return;
            if (typeof element.lat !== 'number' || typeof element.lon !== 'number') return;
            
            const lat = element.lat;
            const lng = element.lon;

            // Check if station is close to route and get distance info
            const routeInfo = this.getStationRouteInfo(lat, lng, routeCoordinates);
            if (routeInfo.distanceToRoute > maxDistanceMiles) {
                return; // Too far from route
            }

            // Extract station info
            const tags = element.tags || {};
            const name = tags.name || tags.brand || w('Gas Station');
            const brand = tags.brand || tags.operator || null;
            const isRvFriendly = this.isRvFriendlyStation(tags);

            stations.push({
                id: element.id,
                verifyId: `osm:${element.type}/${element.id}`,
                taggedRvFriendly: isRvFriendly,
                lat,
                lng,
                name,
                brand,
                isRvFriendly,
                distanceToRoute: routeInfo.distanceToRoute,
                distanceAlongRoute: routeInfo.distanceAlongRoute,
                distanceToDestination: routeInfo.distanceToDestination,
                amenities: {
                    diesel: tags['fuel:diesel'] === 'yes' || tags.diesel === 'yes',
                    hgv: tags.hgv === 'yes',
                    truck: tags.truck === 'yes',
                    restrooms: tags.toilets === 'yes',
                    restaurant: tags.restaurant === 'yes',
                    shop: tags.shop === 'yes' || tags.shop === 'convenience'
                },
                address: tags['addr:full'] || tags['addr:street'] || null,
                phone: tags.phone || null,
                website: tags.website || null
            });
        });

        // Sort by distance along route (not distance to route)
        stations.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);
        
        // Every station along the route; callers limit what they show (the
        // Australian chain filter needs the whole route, not the first 100).
        console.log(`Processed ${stations.length} stations`);

        return stations;
    }

    /**
     * Check if station is RV-friendly based on tags
     */
    isRvFriendlyStation(tags) {
        const brand = (tags.brand || '').toLowerCase();
        const name = (tags.name || '').toLowerCase();
        const operator = (tags.operator || '').toLowerCase();
        
        // Check for HGV tag (most reliable)
        if (tags.hgv === 'yes' || tags.truck === 'yes') {
            return true;
        }
        
        // Check for known RV-friendly brands in name
        const rvKeywords = [
            'love', 'pilot', 'flying j', 'flyingj',
            'ta ', 'petro', 'travelcenters',
            'ambest', 'road ranger', 'roady'
        ];
        
        const searchText = `${name} ${brand} ${operator}`.toLowerCase();
        return rvKeywords.some(keyword => searchText.includes(keyword));
    }

    /**
     * Get station info relative to route
     * @returns {Object} { distanceToRoute, distanceAlongRoute, distanceToDestination, nearestPointIndex }
     */
    getStationRouteInfo(stationLat, stationLng, routeCoordinates) {
        let minDistance = Infinity;
        let nearestPointIndex = 0;
        let distanceAlongRoute = 0;

        // Find nearest point on route
        for (let i = 0; i < routeCoordinates.length - 1; i++) {
            const distance = this.distanceToSegment(
                stationLat, stationLng,
                routeCoordinates[i].lat, routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat, routeCoordinates[i + 1].lng
            );
            
            if (distance < minDistance) {
                minDistance = distance;
                nearestPointIndex = i;
            }
        }

        // Calculate distance along route to nearest point
        for (let i = 0; i < nearestPointIndex; i++) {
            distanceAlongRoute += this.haversineDistance(
                routeCoordinates[i].lat,
                routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat,
                routeCoordinates[i + 1].lng
            );
        }
        
        // Calculate distance from station to destination
        let distanceToDestination = 0;
        for (let i = nearestPointIndex; i < routeCoordinates.length - 1; i++) {
            distanceToDestination += this.haversineDistance(
                routeCoordinates[i].lat,
                routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat,
                routeCoordinates[i + 1].lng
            );
        }

        return {
            distanceToRoute: minDistance,
            distanceAlongRoute: Math.round(distanceAlongRoute * 10) / 10, // Round to 1 decimal
            distanceToDestination: Math.round(distanceToDestination * 10) / 10, // Round to 1 decimal
            nearestPointIndex
        };
    }

    /**
     * Calculate minimum distance from point to route (in miles)
     */
    getMinDistanceToRoute(lat, lng, routeCoordinates) {
        let minDistance = Infinity;

        for (let i = 0; i < routeCoordinates.length - 1; i++) {
            const distance = this.distanceToSegment(
                lat, lng,
                routeCoordinates[i].lat, routeCoordinates[i].lng,
                routeCoordinates[i + 1].lat, routeCoordinates[i + 1].lng
            );
            minDistance = Math.min(minDistance, distance);
        }

        return minDistance;
    }

    /**
     * Calculate distance from point to line segment (in miles)
     */
    distanceToSegment(px, py, x1, y1, x2, y2) {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;

        if (lenSq !== 0) {
            param = dot / lenSq;
        }

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        return this.haversineDistance(px, py, xx, yy);
    }

    /**
     * Calculate distance between two points using Haversine formula (in miles)
     */
    /**
     * Up to `max` stations spread evenly along the route: the route is cut
     * into `max` equal stretches and each stretch keeps its station closest
     * to the road. Stations come in sorted by distanceAlongRoute and stay in
     * that order; stretches with no station are simply skipped.
     */
    spreadAlongRoute(stations, max) {
        if (stations.length <= max) return stations;
        const routeLength = Math.max(...stations.map(s => s.distanceAlongRoute + s.distanceToDestination));
        const stretch = routeLength / max;
        const best = new Map();
        for (const s of stations) {
            const i = Math.min(max - 1, Math.floor(s.distanceAlongRoute / stretch));
            const current = best.get(i);
            if (!current || s.distanceToRoute < current.distanceToRoute) best.set(i, s);
        }
        return [...best.keys()].sort((a, b) => a - b).map(i => best.get(i));
    }

    haversineDistance(lat1, lon1, lat2, lon2) {
        const R = 3959; // Earth's radius in miles
        const dLat = this.toRad(lat2 - lat1);
        const dLon = this.toRad(lon2 - lon1);
        
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    toRad(degrees) {
        return degrees * Math.PI / 180;
    }
}
